import { IconButton, Box } from "@mui/material";
import { useState, useContext, useRef, useEffect, Fragment, type PropsWithChildren } from "react";
import { FiMinus, FiPlus, FiChevronRight } from "react-icons/fi";
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import styles from "./styles.module.css";
import { ITreeViewRecord, TreeViewCommand, TreeViewContext } from "./TreeView";
import { AssetLocation, AssetLocationTypes } from "../../logic/api";
import { useTranslation } from "react-i18next";

interface DragState {
  isDragging: boolean;
  dragOverPosition: 'above' | 'below' | null;
}

let currentDraggedRecord: AssetLocation | null = null;

const draggableTypes = [
  AssetLocationTypes.Zone,
  AssetLocationTypes.Lane,
  AssetLocationTypes.Hook,
  AssetLocationTypes.DynamicBox,
  AssetLocationTypes.KeyBox,
  AssetLocationTypes.Keyroom,
  AssetLocationTypes.KeyLocker,
  AssetLocationTypes.KeyLockerBox,
];

const getNodeSxProps = (isActive: boolean, typeColor: string) => ({
  display: "flex",
  alignItems: "center",
  padding: "10px 20px 10px 10px",
  height: "42px",
  boxSizing: "border-box",
  borderWidth: isActive ? "2px 2px 2px 10px" : "1px 1px 1px 10px",
  borderStyle: "solid",
  borderColor: isActive ? "grey grey grey grey" : `#eee #eee #eee ${typeColor}`,
  borderRadius: "5px",
  backgroundColor: "#fff",
  cursor: "pointer",
  transition: "all 0.05s ease",
  color: isActive ? "black" : "inherit",
});

const TreeViewNode = (props: PropsWithChildren<{
  record: ITreeViewRecord,
  expanded?: boolean,
  onDragReorder?: (draggedRecord: ITreeViewRecord, targetRecord: ITreeViewRecord, position: 'above' | 'below') => void,
  disabled?: boolean,
}>) => {
  const { t } = useTranslation();
  const [record, setRecord] = useState(props.record);
  const [expanded, setExpanded] = useState(Boolean(props.expanded));
  const tvc = useContext(TreeViewContext);
  const nodeRef = useRef<HTMLLIElement>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);
  const [removed, setRemoved] = useState(false);
  const [dragState, setDragState] = useState<DragState>({ isDragging: false, dragOverPosition: null });

  const location = record as AssetLocation;

  // Determine if this node can be dragged
  const isDraggable = (): boolean => {
    return draggableTypes.includes(location.type);
  };

  // Determine if a drop is valid on this node
  const isValidDropTarget = (draggedLocation: AssetLocation): boolean => {
    // Only allow draggable types
    if (!draggableTypes.includes(draggedLocation.type)) {
      return false;
    }

    // Don't allow dropping on self
    if (draggedLocation.id === location.id) {
      return false;
    }

    // Must have same parent
    if (draggedLocation.parentId !== location.parentId) {
      return false;
    }

    // Must be same type (prevent mixing types)
    if (draggedLocation.type !== location.type) {
      return false;
    }

    return true;
  };

  const selectNode = () => {
    if (tvc.setState) {
      tvc.setState({
        ...tvc.state,
        treeRootId: tvc.state == null ? 0 : tvc.state.treeRootId,
        selectedRecord: record,
      });
    }
  }

  useEffect(() => {
    if (tvc.state?.selectedRecord?.path) {
      const selectedPath = tvc.state.selectedRecord.path.toString();
      const recordPath = record.path == null ? "" : record.path.toString();

      if (!selectedPath.indexOf(recordPath) && selectedPath != "/") {
        if (!expanded) {
          setExpanded(true);
          if (selectedPath === recordPath) {
            selectNode();
            if (nodeRef?.current) {
              nodeRef.current.scrollIntoView({ block: "center" });
            }
          }
        }
      }
    }
  }, []);

  useEffect(() => {
    if (tvc.state) {
      if (tvc.state.command) {
        if (tvc.state.command.targetId == props.record.id) {
          if (tvc.state.command.command == TreeViewCommand.update) {
            if (tvc.state.command.payLoad) {
              setRecord(tvc.state.command.payLoad);
              if (tvc.setState) {
                tvc.setState({
                  ...tvc.state,
                  command: undefined
                });
              }
            }
          } else if (tvc.state.command.command == TreeViewCommand.remove) {
            {
              setRemoved(true);
              if (tvc.setState) {
                tvc.setState({
                  ...tvc.state,
                  command: undefined
                });
              }
            }
          } else if (tvc.state.command.command == TreeViewCommand.expanded) {
            {
              setExpanded(true);
              if (tvc.setState) {
                tvc.setState({
                  ...tvc.state,
                  command: undefined
                });
              }
            }
          } else if (tvc.state.command.command == TreeViewCommand.collapsed) {
            {
              setExpanded(false);
              if (tvc.setState) {
                tvc.setState({
                  ...tvc.state,
                  command: undefined
                });
              }
            }
          }
        }
      }
    }
  }, [tvc.state != null ? tvc.state.command : null]);

  // Drag and Drop handlers
  const handleDragStart = (e: React.DragEvent) => {
    if (!isDraggable()) {
      e.preventDefault();
      return;
    }

    currentDraggedRecord = location;
    setDragState({ ...dragState, isDragging: true });
    e.dataTransfer.setData('text/plain', ''); // Required for drag to work
    e.dataTransfer.effectAllowed = 'move';

  };

  const handleDragEnd = () => {
    currentDraggedRecord = null;
    setDragState({ isDragging: false, dragOverPosition: null });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!nodeRef.current || !currentDraggedRecord) return;

    // Don't show drop indicator on the item being dragged itself
    if (currentDraggedRecord.id === location.id) {
      setDragState({ isDragging: false, dragOverPosition: null });
      e.dataTransfer.dropEffect = 'none';
      return;
    }

    const rect = nodeRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const height = rect.height;
    const position = y < height / 2 ? 'above' : 'below';

    if (isValidDropTarget(currentDraggedRecord)) {
      setDragState({ isDragging: false, dragOverPosition: position });
      e.dataTransfer.dropEffect = 'move';
    } else {
      setDragState({ isDragging: false, dragOverPosition: null });
      e.dataTransfer.dropEffect = 'none';
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear if we're actually leaving this element, not moving to a child
    if (!nodeRef.current?.contains(e.relatedTarget as Node)) {
      setDragState({ isDragging: false, dragOverPosition: null });
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!currentDraggedRecord || !dragState.dragOverPosition) {
      setDragState({ isDragging: false, dragOverPosition: null });
      return;
    }

    if (isValidDropTarget(currentDraggedRecord)) {
      props.onDragReorder?.(currentDraggedRecord, record, dragState.dragOverPosition);
    } else {
      console.info('Invalid drop target');
    }

    setDragState({ isDragging: false, dragOverPosition: null });
  };

  if (!tvc.state) {
    return <Fragment></Fragment>
  }

  const design = tvc.getNodeDesign == null ? {} : tvc.getNodeDesign(record);
  design.colour = design.colour || "grey";

  const hasChildren = record.hasChildren;
  const selectedRecordId = !tvc.state.selectedRecord ? -1 : tvc.state.selectedRecord.id;

  if (removed == true) {
    return <Fragment></Fragment>
  }

  const getDragOverStyle = () => {
    if (currentDraggedRecord && currentDraggedRecord.id === location.id) {
      return {};
    }

    if (dragState.dragOverPosition === 'above') {
      return { borderTop: '2px solid #1976d2' };
    } else if (dragState.dragOverPosition === 'below') {
      return { borderBottom: '2px solid #1976d2' };
    }
    return {};
  };

  const { enableDragAndDrop } = tvc;

  return (
    <Fragment>
      <li
        className={styles.li}
        style={{
          opacity: record.active == false || props.disabled ? 0.5 : (currentDraggedRecord?.id === location.id ? 0.3 : 1),
          cursor: currentDraggedRecord ? 'grabbing' : 'default',
          pointerEvents: props.disabled ? "none" : "auto",
          ...getDragOverStyle()
        }}
        ref={nodeRef}
        {...(enableDragAndDrop ? {
          onDragOver: handleDragOver,
          onDragLeave: handleDragLeave,
          onDrop: handleDrop
        } : {})}
      >
        <Box sx={{
          display: 'flex',
          height: '42px',
          alignItems: 'center',
          '&:hover .dragHandle': {
            opacity: 0.7
          }
        }}>
          {/* Drag Handle */}
          <Box
            sx={{
              flexShrink: 0,
              width: '32px',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {enableDragAndDrop && isDraggable() && props.onDragReorder && (
              <Box
                ref={dragHandleRef}
                draggable={true}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                className="dragHandle"
                sx={{
                  color: design.colour,
                  opacity: 0,
                  transition: 'opacity 0.2s ease',
                  padding: '4px',
                  cursor: 'grab'
                }}
                title={t("general.dragToReorder")}
              >
                <DragIndicatorIcon />
              </Box>
            )}
          </Box>

          <Box sx={{ flexShrink: 0 }}>
            {hasChildren == true ? (
              <IconButton
                data-testid={`tree-node-expand-${record.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setExpanded(!expanded);
                  return false;
                }}
                size="small"
              >
                {expanded == true ? (<FiMinus />) : (<FiPlus />)}
              </IconButton>
            ) : (<IconButton disabled={true} size="small"><FiChevronRight /></IconButton>)}
          </Box>

          <Box sx={{ flex: '0 0 auto' }}>
            <Box
              data-testid={`tree-node-${record.id}`}
              sx={getNodeSxProps(selectedRecordId == record.id, design.colour)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                selectNode();
                return false;
              }}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '8px',
                flexWrap: 'nowrap'
              }}>
                {design.icon != null && (
                  <Box sx={{
                    color: design.colour,
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <design.icon />
                  </Box>
                )}
                <Box>
                  {props.children
                    ? props.children
                    : (<b>{record.tmpName != null ? record.tmpName : record.name}</b>)
                  }
                </Box>
                {design.attachment && (
                  <design.attachment record={record}></design.attachment>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </li>
      {
        (expanded && hasChildren && tvc?.treeViewLoader) && (<ul className={styles.ul}>
          <tvc.treeViewLoader
            flags={tvc.state.flags}
            parentId={record.id}
            parentRecord={record}
            defaultExpanded={tvc.state.defaultExpanded}
            onDragReorder={props.onDragReorder}
          />
        </ul>)
      }
    </Fragment>
  );
};

export default TreeViewNode;
